import { Injectable } from "@angular/core";
import { EntityService } from "@app/entity/services/entity.service";
import { AlulaReceiver } from "@app/project/model/alula-receiver";
import { AlulaReceiverBindDbService } from "@app/project/services/alula-receiver-bind-db.service";
import { AlulaReceiverDbService } from "@app/project/services/alula-receiver-db.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { checkCurrentID } from "@app/shared/resources/data-actions";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { catchError, concatMap, filter, map, switchMap } from "rxjs/operators";
import { AlulaReceiverActions, AlulaReceiverBindActions, DataActions } from "../actions";

const toAction = DataActions.toAction2();

@Injectable()
export class AlulaReceiverEffects {
    readonly entityName = "Communication Path";

    constructor(
        private readonly actions$: Actions,
        private readonly alulaReceiverDbService: AlulaReceiverDbService,
        private readonly alulaReceiverBindDbService: AlulaReceiverBindDbService,
        private readonly entityService: EntityService
    ) {}

    wrapResult(this: void, source$: Observable<AlulaReceiver>) {
        return source$.pipe(map(result => ({ receiver: result })));
    }

    wrapBulkResult(this: void, source$: Observable<any>) {
        return source$.pipe(
            map(result => {
                const receivers = {};
                result.receivers.forEach(receiver => {
                    receivers[receiver.id] = receiver;
                });
                return {
                    receivers,
                    dealerId: result.dealerId,
                };
            })
        );
    }

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.load),
            switchMap(({ dealerId }) =>
                toAction(
                    this.alulaReceiverDbService
                        .getAlulaReceiversByDealerId(dealerId)
                        .pipe(this.wrapBulkResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), dealerId)),
                    AlulaReceiverActions.loadSuccess,
                    AlulaReceiverActions.loadFailure
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.create),
            concatMap(({ receiver, bind, callback }) => {
                if (!!bind) {
                    return this.alulaReceiverDbService.createAlulaReceiver(receiver).pipe(
                        filter((_receiver: AlulaReceiver) => !!_receiver),
                        switchMap((_receiver: AlulaReceiver) => {
                            const newAction = AlulaReceiverActions.createSuccess({ receiver: _receiver });
                            callback?.emit(newAction);
                            const updatedBind = { ...bind, receiverId: _receiver.id };

                            return toAction(
                                this.alulaReceiverBindDbService.createAlulaReceiverBind(updatedBind).pipe(
                                    map(result => ({ bind: result })),
                                    checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiver?.dealerId)
                                ),
                                AlulaReceiverBindActions.createSuccess,
                                AlulaReceiverBindActions.createFailure,
                                callback
                            );
                        })
                    );
                }

                return toAction(
                    this.alulaReceiverDbService.createAlulaReceiver(receiver).pipe(this.wrapResult),
                    AlulaReceiverActions.createSuccess,
                    AlulaReceiverActions.createFailure,
                    callback
                );
            }),
            catchError(DataActions.toError2(AlulaReceiverActions.createFailure))
        )
    );

    createFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.createFailure),
            map(DataActions.handleErrorMessage2(ActionWord.created, this.entityName))
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.update),
            concatMap(({ receiver, callback }) =>
                toAction(
                    this.alulaReceiverDbService
                        .updateAlulaReceiver(receiver, receiver.id)
                        .pipe(this.wrapResult, checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiver?.dealerId)),
                    AlulaReceiverActions.updateSuccess,
                    AlulaReceiverActions.updateFailure,
                    callback
                )
            )
        )
    );

    updateFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.updateFailure),
            map(DataActions.handleErrorMessage2(ActionWord.saved, this.entityName))
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.deleteItem),
            concatMap(({ receiver, callback }) =>
                toAction(
                    this.alulaReceiverDbService.deleteAlulaReceiver(receiver?.id).pipe(
                        map(() => receiver),
                        checkCurrentID(this.entityService.selectCurrentAlulaDealerId$(), receiver?.dealerId)
                    ),
                    AlulaReceiverActions.deleteSuccess,
                    AlulaReceiverActions.deleteFailure,
                    callback
                )
            )
        )
    );

    deleteFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AlulaReceiverActions.deleteFailure),
            map(DataActions.handleErrorMessage2(ActionWord.removed, this.entityName))
        )
    );
}
